import {
  css,
  inputAdornmentClasses,
  inputBaseClasses,
  OutlinedInput as MuiOutlinedInput,
  outlinedInputClasses,
  styled,
} from '@mui/material';

import { textInputEndAdornmentIconClasses } from '../TextInput';
import { textInputLinerRootClasses } from './theme';

import type { Theme } from '@mui/material';
import type { TextInputLinerOwnerState, TextInputLinerProps } from './types';

// StyledTextInputLiner - 모든 스타일을 인라인으로 적용
export const StyledTextInputLiner = styled(
  MuiOutlinedInput,
  {},
)<{ ownerState: TextInputLinerOwnerState }>(({ ownerState, theme }) => {
  const { size, multiline, error, success, loading } = ownerState;

  return css`
    /* 기본 root 스타일 */
    ${getRootStyles(theme)}

    /* multiline 스타일 */
    ${multiline && getMultilineStyles(ownerState)}

    /* input 슬롯 스타일 */
    & .${outlinedInputClasses.input} {
      ${getInputStyles()}
    }

    /* error 스타일 */
    ${error && getErrorStyles(theme)}

    /* size별 스타일 */
    ${getSizeStyles(size, theme)}

    /* success 스타일 */
    ${success && getSuccessStyles(theme)}

    /* loading 스타일 */
    ${loading && getLoadingStyles(theme)}
  `;
});

// 스타일 헬퍼 함수들

const getRootStyles = (theme: Theme) => css`
  align-items: center;
  font-weight: 400;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: ${theme.color['text/default']};
  background-color: ${theme.color['surface/default']};
  padding-left: 8px;
  padding-right: 8px;
  border-radius: 1px;
  transition: none;

  & input {
    &::placeholder {
      color: ${theme.color['text/weakest/default']};
      opacity: 1;
    }
  }

  & textarea {
    &::placeholder {
      color: ${theme.color['text/weakest/default']};
      opacity: 1;
      white-space: pre-line;
    }
  }

  // border

  & .${outlinedInputClasses.notchedOutline} {
    border-color: ${theme.color['border/weak/default']};
    border-width: 1px;
  }

  &:hover .${outlinedInputClasses.notchedOutline} {
    border-color: ${theme.color['border/weak/default']};
  }

  &.${outlinedInputClasses.disabled} .${outlinedInputClasses.notchedOutline} {
    border-color: transparent;
    border-width: 1px;
  }

  ${`&.${inputBaseClasses.focused} .${outlinedInputClasses.notchedOutline}, &.${inputBaseClasses.focused}:hover .${outlinedInputClasses.notchedOutline}`} {
    border-color: ${theme.color['border/weak/default']};
    border-width: 1px;
    border-bottom-color: ${theme.color['focusRing/default']};
  }

  // hover

  &:hover:not(.${inputBaseClasses.disabled}, .${inputBaseClasses.error}) {
    background-color: ${theme.color['surface/hover']};
  }

  // focus

  &.${inputBaseClasses.focused} {
    background-color: ${theme.color['surface/default']};
  }

  // disabled

  &.${inputBaseClasses.disabled} {
    background-color: ${theme.color['surface/neutral/weakest/disabled']};
    pointer-events: none;

    &.${outlinedInputClasses.notchedOutline} {
      border-color: transparent;
    }

    & input,
    & textarea {
      &::placeholder {
        -webkit-text-fill-color: ${theme.color['text/disabled']};
      }
    }

    & > .${inputAdornmentClasses.root} {
      opacity: 0.3;
    }

    /* loading / error / success일 때 adornment는 선명하게 */
    &.${textInputLinerRootClasses.loading}
      > .${inputAdornmentClasses.root},
      &.${inputBaseClasses.error}
      > .${inputAdornmentClasses.root},
      &.${textInputLinerRootClasses.success}
      > .${inputAdornmentClasses.root} {
      opacity: 1;
    }
  }

  // endAdornment - loading

  & .${textInputEndAdornmentIconClasses.loadingIcon} {
    display: inline-block;
    position: relative;
  }

  & .${textInputEndAdornmentIconClasses.clearIcon} {
    color: ${theme.color['icon/strong/default']};
    cursor: pointer;

    &:hover {
      color: ${theme.color['icon/strong/hover']};
    }
  }
`;

const getMultilineStyles = (ownerState: TextInputLinerProps) => css`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: fit-content;

  ${!!ownerState?.resizable &&
  !!ownerState?.multiline &&
  !ownerState?.readOnly &&
  !ownerState?.disabled &&
  css`
    resize: both;
    min-height: 68px;
    max-height: 568px;
    min-width: 72px;
    max-width: min(960px, 100%);
    overflow-x: hidden;
  `}
  & > .${outlinedInputClasses.multiline} {
    overflow: auto !important;
  }

  & > .${inputAdornmentClasses.root}:has(*) {
    width: 100%;
    justify-content: flex-end;
    margin-left: 0;
    margin-top: auto;
    align-self: flex-end;
    flex-shrink: 0;
    height: fit-content;
  }
`;

const getInputStyles = () => css`
  padding: 0 4px;
  height: 20px;

  &.${textInputLinerRootClasses.overflow} {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

const getErrorStyles = (theme: Theme) => css`
  &:not(.${textInputLinerRootClasses.loading}).${inputBaseClasses.error}
    .${outlinedInputClasses.notchedOutline} {
    border-bottom-color: ${theme.color['border/danger/default']};
  }

  ${`&.${inputBaseClasses.error}:not(.${inputBaseClasses.disabled})
    .${outlinedInputClasses.notchedOutline}`} {
    border-color: ${theme.color['border/weak/default']};
    border-bottom-color: ${theme.color['border/danger/default']};
  }

  &.${inputBaseClasses.error}.${textInputLinerRootClasses.loading}
    .${outlinedInputClasses.notchedOutline} {
    border-color: ${theme.color['border/weak/default']};
  }

  &:hover:not(.${outlinedInputClasses.disabled}, .${textInputLinerRootClasses.loading}) {
    background-color: ${theme.color['surface/danger/weakest/default']};

    & .${outlinedInputClasses.notchedOutline} {
      border-bottom-color: ${theme.color['border/danger/default']};
    }
  }

  &.${inputBaseClasses.error}.${inputBaseClasses.focused} {
    background-color: ${theme.color['surface/default']};

    & .${outlinedInputClasses.notchedOutline} {
      border-bottom-color: ${theme.color['focusRing/default']};
    }

    &:hover {
      background-color: ${theme.color['surface/danger/weakest/default']};
    }
  }

  // endAdornment

  & .${textInputEndAdornmentIconClasses.errorIcon} {
    color: ${theme.color['surface/danger/default']};
  }

  &.${inputBaseClasses.disabled} .${textInputEndAdornmentIconClasses.errorIcon} {
    color: ${theme.color['icon/danger/disabled']};
  }

  &.${inputBaseClasses.disabled}:not(.${textInputLinerRootClasses.loading}).${inputBaseClasses.error}
    .${outlinedInputClasses.notchedOutline} {
    border-bottom-color: ${theme.color['icon/danger/disabled']};
  }
`;

const getSizeStyles = (size: 'sm' | 'md' | 'lg', theme: Theme) => {
  const sizeStylesMap = {
    sm: css`
      // root
      height: 28px;

      &.${outlinedInputClasses.root} {
        ${theme.typography['label/regular/sm']};
      }

      // input

      &.${outlinedInputClasses.root}:not(.${outlinedInputClasses.multiline}) {
        padding-top: 4px;
        padding-bottom: 4px;
      }

      // multiline

      &.${outlinedInputClasses.root}:is(.${outlinedInputClasses.multiline}) {
        padding-top: 8px;
        padding-bottom: 8px;
      }

      // endAdornment

      & > .${inputAdornmentClasses.root} {
        font-size: 11px;
        line-height: 16.5px;
        color: ${theme.color['icon/weakest/default']};

        & > *:not(.notranslate) + * {
          margin-left: 6px;
        }

        & svg {
          width: 16px;
          height: 16px;
          font-size: 16px;
        }

        & .${textInputEndAdornmentIconClasses.loadingIcon} {
          width: 16px;
          height: 16px;
          font-size: 16px;
        }
      }
    `,
    md: css`
      // root
      height: 32px;

      &.${outlinedInputClasses.root} {
        ${theme.typography['label/regular/md']};
      }

      // input

      &.${outlinedInputClasses.root}:not(.${outlinedInputClasses.multiline}) {
        padding-top: 6px;
        padding-bottom: 6px;
      }

      // multiline

      &.${outlinedInputClasses.root}:is(.${outlinedInputClasses.multiline}) {
        padding-top: 8px;
        padding-bottom: 8px;
      }

      // endAdornment

      & > .${inputAdornmentClasses.root} {
        font-size: 12px;
        line-height: 18px;
        color: ${theme.color['icon/weakest/default']};

        & > *:not(.notranslate) + * {
          margin-left: 6px;
        }

        & svg {
          width: 16px;
          height: 16px;
          font-size: 16px;
        }
      }
    `,
    lg: css`
      // root
      height: 40px;

      &.${outlinedInputClasses.root} {
        ${theme.typography['label/regular/md']};
      }

      // input

      &.${outlinedInputClasses.root}:not(.${outlinedInputClasses.multiline}) {
        padding-top: 10px;
        padding-bottom: 10px;
      }

      // multiline

      &.${outlinedInputClasses.root}:is(.${outlinedInputClasses.multiline}) {
        padding-top: 10px;
        padding-bottom: 10px;
      }

      // endAdornment

      & > .${inputAdornmentClasses.root} {
        font-size: 14px;
        line-height: 21px;
        color: ${theme.color['icon/weakest/default']};

        & > *:not(.notranslate) + * {
          margin-left: 8px;
        }

        & svg {
          width: 20px;
          height: 20px;
          font-size: 20px;
        }
      }
    `,
  };

  return sizeStylesMap[size] || css``;
};

const getSuccessStyles = (theme: Theme) => css`
  &:not(.${textInputLinerRootClasses.loading}):not(.${inputBaseClasses.disabled})
    .${outlinedInputClasses.notchedOutline} {
    border-bottom-color: ${theme.color['border/success/default']};
  }

  &:not(.${inputBaseClasses.disabled}, .${textInputLinerRootClasses.loading})
    .${outlinedInputClasses.notchedOutline} {
    border-color: ${theme.color['border/weak/default']};
    border-bottom-color: ${theme.color['border/success/default']};
  }

  &.${inputBaseClasses.disabled}:not(.${textInputLinerRootClasses.loading})
    .${outlinedInputClasses.notchedOutline} {
    border-bottom-color: ${theme.color['icon/success/disabled']};
  }

  &.${inputBaseClasses.focused} {
    background-color: ${theme.color['surface/default']};

    & .${outlinedInputClasses.notchedOutline} {
      border-bottom-color: ${theme.color['focusRing/default']};
    }
  }

  &:hover:not(
      .${outlinedInputClasses.disabled},
        .${inputBaseClasses.error},
        .${textInputLinerRootClasses.loading}
    ) {
    background-color: ${theme.color['surface/success/weakest/default']};
  }

  // endAdornment

  & .${textInputEndAdornmentIconClasses.successIcon} {
    color: ${theme.color['icon/success/default']};
  }

  &.${inputBaseClasses.disabled} .${textInputEndAdornmentIconClasses.successIcon} {
    color: ${theme.color['icon/success/disabled']};
  }
`;

const getLoadingStyles = (theme: Theme) => css`
  ${`&.${textInputLinerRootClasses.loading}:hover:not(.${outlinedInputClasses.disabled})`} {
    background-color: ${theme.color['surface/hover']};
  }

  &.${textInputLinerRootClasses.loading} {
    &:not(.${outlinedInputClasses.disabled}) {
      background-color: ${theme.color['surface/default']};
    }
  }
`;
